import Dinero from 'dinero.js'
import { round } from 'lodash-es'

Dinero.defaultCurrency = 'ZAR'
Dinero.globalLocale = 'en-ZA'
Dinero.defaultPrecision = 2

const VAT = 1.15

/**
 * Convert a rounded or decimal amount to cents
 * @param {Number} amount decimal or rounded amount
 * @returns {Object} Dinero
 */
export const toCents = (amount: any) => new Dinero({ amount: round(amount ? amount * 100 : 0) })

/**
 * Convert cents to decimal
 * @param {Number} amount amount in cents
 * @returns {Number} rounded decimal
 */
export const fromCents = (amount: any) => round(amount / 100, 2)

/**
 * Add VAT to an amount in cents
 * @param {Number} amount amount in cents
 * @returns {Object} Dinero
 */
export const addVat = (amount: any) => Dinero({ amount }).multiply(VAT)

/**
 * Subtract VAT from an amount in cents
 * @param {Number} amount amount in cents
 * @returns {Object} Dinero
 */
export const sutractVat = (amount: any) => Dinero({ amount }).divide(VAT)

export const formatCurrency = (cents: any, omitSymbol = false) => {
  const dinero = Dinero({ amount: typeof cents === 'string' ? parseInt(cents) : cents })
  const formatted = omitSymbol ? dinero.toFormat('0,0.00') : dinero.toFormat('$0,0.00')
  return formatted
    .replace(/,(?!\d{2}$)/g, ' ') // Replace thousand separators with spaces
    .replace(/,(?=\d{2}$)/g, '.') // Replace decimal separator with period
}

/**
 * Format an amount in cents for display, matching how the currency inputs render
 * (NumericFormat with a space thousand separator and a fixed decimal scale of 2).
 * The currency symbol is omitted, as call sites render it themselves.
 * @param {(String|Number)} val amount in cents
 * @returns {String} formatted amount, eg. 1200000 -> '12 000.00', or '' when empty
 */
export const formatCurrencyValue = (val?: string | number): string => {
  if (!val) return ''
  const numVal = typeof val === 'string' ? parseFloat(val) : val
  // Dinero rejects non-integer amounts, and parsed cents aren't guaranteed to be whole
  return formatCurrency(Math.round(numVal), true)
}

/**
 * Format an amount in cents as a plain decimal string, for use as the value of a
 * NumericFormat input. No separators are added, as NumericFormat applies its own.
 * @param {(String|Number)} val amount in cents
 * @returns {String} decimal amount, eg. 1200000 -> '12000.00', or '' when empty
 */
export const formatCurrencyInputValue = (val?: string | number): string => {
  if (!val) return ''
  const numVal = typeof val === 'string' ? parseFloat(val) : val
  return fromCents(numVal).toFixed(2)
}

export { Dinero }
